import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { CoreUnsupported, Unauthorized } from '../api/client'
import type { ScopeNote } from '../api/types'

/** Единая обработка загрузки/ошибок/устаревших данных/суженной выборки для всех экранов. */
export function QueryState({
  isLoading,
  error,
  stale,
  ageMin,
  scope,
  children,
}: {
  isLoading: boolean
  error: unknown
  stale?: boolean
  ageMin?: number
  /**
   * Пометка BFF «выборка сужена правами» (`data.scope`). Передаётся экраном, потому что
   * пометка приезжает с данными, а не с запросом. Без неё счётчик и итог на экране —
   * часть, выданная за целое.
   */
  scope?: ScopeNote
  children: ReactNode
}) {
  // сессия кончилась — уводим на логин, а не показываем страшную ошибку
  if (error instanceof Unauthorized) return <Navigate to="/login" replace />

  // Ядро отказалось обслуживать запрос. Пустая таблица здесь означала бы «данных нет»
  // (для дебиторки — «долгов нет»), поэтому говорим прямо: это отказ, а не отсутствие.
  if (error instanceof CoreUnsupported) {
    return (
      <div className="notice">
        Ядро не может обслужить этот запрос{error.why ? `: ${error.why}` : ''}. Данные есть,
        но такой фильтр или сортировку ядро не поддерживает — измените условие. Пустой список
        здесь не означал бы «ничего не найдено», поэтому мы его не показываем.
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Ошибка: {(error as Error).message}</div>
        <div className="muted" style={{ marginTop: 8 }}>
          Если это «core_unreachable» — ядро (cir-core-api) недоступно, данные показать нечем.
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="card">
        {[70, 90, 55, 80].map((w, i) => (
          <div key={i} className="skeleton" style={{ width: `${w}%`, marginBottom: 10 }} />
        ))}
      </div>
    )
  }

  return (
    <>
      {stale && (
        <div className="notice">
          Ядро отдало пометку «устаревшие данные» (синк отстал на {Math.round(ageMin ?? 0)} мин).
          Список пуст не потому, что сделок нет — данным нельзя доверять до догона синка.
        </div>
      )}
      {/* Выборку сузили права роли. Говорим об этом ВЫШЕ данных: счётчики и итоги под ней
          считаются по зоне, а не по всей базе, и без этой строки часть долга читается как
          весь долг. Текст сужения — словами BFF, а не нашими: он знает, чем именно сужено. */}
      {scope?.limited && (
        <div className="notice">
          Вы видите не всё: выборка сужена вашими правами{scope.note ? ` — ${scope.note}` : ''}.
          Счётчик записей и любые суммы ниже относятся только к вашей зоне, а не ко всей базе.
        </div>
      )}
      {children}
    </>
  )
}
